import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { api, type ProfileMasterItem, type UserProfileItem } from "../lib/api";
import { toUserMessage } from "../lib/errors";
import { UI_TEXT_TONE } from "../lib/uiTone";

type ProfileRouteState = {
  returnTo?: string;
};

function sortProfiles(items: UserProfileItem[]) {
  return [...items].sort((a, b) => a.displayOrder - b.displayOrder || a.label.localeCompare(b.label, "ja"));
}

function areProfilesEqual(a: UserProfileItem[], b: UserProfileItem[]) {
  if (a.length !== b.length) return false;
  return a.every((item, index) => {
    const other = b[index];
    return !!other && item.key === other.key && item.label === other.label && item.value === other.value;
  });
}

export default function ProfileSettings() {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = (typeof location.state === "object" && location.state ? location.state : {}) as ProfileRouteState;
  const returnTo = String(routeState.returnTo || "/home");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<UserProfileItem[]>([]);
  const [initialProfiles, setInitialProfiles] = useState<UserProfileItem[]>([]);
  const [masterItems, setMasterItems] = useState<ProfileMasterItem[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  useEffect(() => {
    let active = true;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [master, userProfiles] = await Promise.all([api.getProfileMaster(), api.getUserProfile()]);
        if (!active) return;
        const sortedProfiles = sortProfiles(userProfiles);
        setMasterItems(master);
        setProfiles(sortedProfiles);
        setInitialProfiles(sortedProfiles);
      } catch (e) {
        if (!active) return;
        setError(toUserMessage(e));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const isDirty = useMemo(() => !areProfilesEqual(profiles, initialProfiles), [initialProfiles, profiles]);
  const availableItems = useMemo(
    () => masterItems.filter((item) => !profiles.some((profile) => profile.key === item.key)),
    [masterItems, profiles]
  );

  const closePage = () => {
    if (isDirty) {
      const ok = window.confirm("保存されていない変更があります。保存せずに戻りますか？");
      if (!ok) return;
    }
    navigate(returnTo, { replace: true });
  };

  const updateProfileValue = (key: string, value: string) => {
    setProfiles((prev) => prev.map((item) => (item.key === key ? { ...item, value } : item)));
  };

  const removeProfile = (key: string) => {
    setProfiles((prev) => prev.filter((item) => item.key !== key));
  };

  const addProfile = (item: ProfileMasterItem) => {
    setProfiles((prev) =>
      sortProfiles([
        ...prev,
        {
          key: item.key,
          label: item.label,
          value: "",
          displayOrder: item.displayOrder,
        },
      ])
    );
    setIsAddModalOpen(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const savedProfiles = await api.updateUserProfile({
        profiles: profiles.map((item) => ({
          key: item.key,
          value: item.value,
        })),
      });
      const sortedProfiles = sortProfiles(savedProfiles);
      setProfiles(sortedProfiles);
      setInitialProfiles(sortedProfiles);
    } catch (e) {
      setError(toUserMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(244,227,215,0.9),_rgba(253,248,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-xl items-center justify-center">
        <div className="relative w-full max-w-full rounded-[2.4rem] border border-white/80 bg-white/90 p-4 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <button
            type="button"
            onClick={closePage}
            aria-label="閉じる"
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#EADBC8] bg-[#FFF8F4] text-[#7C5A4E] transition hover:bg-white"
          >
            ×
          </button>

          <div className="mb-5 pr-12">
            <h1 className={`text-2xl font-black tracking-tight ${UI_TEXT_TONE.title}`}>プロファイル設定</h1>
            <p className={`mt-2 text-sm leading-6 ${UI_TEXT_TONE.body}`}>
              あなたの情報を整えるためのプロファイルです。必要な項目だけ追加し、内容を編集して保存できます。
            </p>
          </div>

          {loading ? <p className="text-sm text-[#7B6861]">読み込み中です...</p> : null}
          {!loading && error ? <p className="mb-4 text-sm text-[#A1493E]">{error}</p> : null}

          {!loading ? (
            <>
              <div className="space-y-3">
                {profiles.map((item) => (
                  <div
                    key={item.key}
                    className="rounded-[1.8rem] border border-[#EADBC8] bg-white px-4 py-4 shadow-[0_10px_24px_rgba(95,139,87,0.05)]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <label htmlFor={`profile-${item.key}`} className="text-sm font-black text-[#5D4D46]">
                          {item.label}
                        </label>
                        <input
                          id={`profile-${item.key}`}
                          type="text"
                          value={item.value}
                          onChange={(event) => updateProfileValue(item.key, event.target.value)}
                          className="mt-3 w-full rounded-[1.1rem] border border-[#EADBC8] bg-[#FFFCFA] px-4 py-3 text-sm text-[#4A3E39] outline-none transition focus:border-[#D1907B] focus:bg-white"
                          placeholder={`${item.label}を入力`}
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => removeProfile(item.key)}
                        className="shrink-0 rounded-full border border-[#EADBC8] px-3 py-1.5 text-xs font-bold text-[#8B7065] transition hover:bg-[#FFF8F4]"
                      >
                        削除
                      </button>
                    </div>
                  </div>
                ))}

                {profiles.length === 0 ? (
                  <div className="rounded-[1.8rem] border border-dashed border-[#EADBC8] bg-[#FFFDFB] px-4 py-6 text-sm leading-6 text-[#7B6861]">
                    まだプロファイル項目はありません。必要な項目を追加して使い始められます。
                  </div>
                ) : null}
              </div>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(true)}
                  disabled={availableItems.length === 0}
                  className="flex-1 rounded-[1.6rem] border border-[#D9C5B9] bg-white px-4 py-4 text-sm font-black text-[#5D4D46] transition hover:bg-[#FFFCFA] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  プロファイルを追加
                </button>
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={!isDirty || saving}
                  className="flex-1 rounded-[1.6rem] bg-[#5D4D46] px-4 py-4 text-sm font-black text-[#FDF8F5] shadow-lg transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? "保存中..." : "保存する"}
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {isAddModalOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#2B211D]/45 p-4">
          <div className="w-full max-w-md rounded-[2rem] border border-[#EADBC8] bg-[#FFFDFB] shadow-[0_18px_40px_rgba(74,62,57,0.22)]">
            <div className="flex items-center justify-between border-b border-[#EADBC8] px-5 py-4">
              <div>
                <h2 className="text-lg font-black text-[#7C5A4E]">プロファイルを追加</h2>
                <p className="mt-1 text-xs text-[#7B6861]">追加したい項目を選んでください。</p>
              </div>
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#EADBC8] bg-white text-[#7C5A4E]"
                aria-label="閉じる"
              >
                ×
              </button>
            </div>

            <div className="max-h-[60vh] space-y-2 overflow-y-auto px-4 py-4">
              {availableItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => addProfile(item)}
                  className="w-full rounded-[1.5rem] border border-[#EADBC8] bg-white px-4 py-4 text-left transition hover:bg-[#FFFCFA]"
                >
                  <span className="block text-sm font-black text-[#5D4D46]">{item.label}</span>
                  <span className="mt-1 block text-xs text-[#8B7065]">{item.key}</span>
                </button>
              ))}

              {availableItems.length === 0 ? (
                <div className="rounded-[1.5rem] border border-dashed border-[#EADBC8] bg-[#FFFDFB] px-4 py-6 text-sm text-[#7B6861]">
                  追加できるプロファイル項目はありません。
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
